import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

import logo from "../../src/assets/images/logo.png";
import dashboardBG from "../../src/assets/images/background.png";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();

    if (!/^\S+@\S+\.\S+$/.test(normalized)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: normalized });
      setSent(true);
    } catch (err) {
      const message =
        err.response?.data?.message || 'Failed to send reset email. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050613] px-4 py-8 sm:px-6 lg:px-8"
      style={{
        backgroundImage: `url(${dashboardBG})`,
        backgroundPosition: "bottom center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl place-items-center">
        {/* LEFT CARD */}
        <div
          className="auth-card-shell mx-auto w-full max-w-md border border-[#B8B8B847] bg-[#212A5533] backdrop-blur-sm animate-fadeInSoft"
          style={{ animationDelay: "60ms" }}
        >
          <div
            className="auth-card-panel border border-[#EBEBEB1C] bg-[#FFFFFF14] p-6 shadow-[0_25px_50px_rgba(3,8,25,0.55)] backdrop-blur animate-fadeInSoft"
            style={{ animationDelay: "120ms" }}
          >
            <div className="mb-6 flex items-center justify-between animate-fadeInSoft" style={{ animationDelay: "160ms" }}>
              <h1 className="text-4xl sm:text-5xl leading-[1.2] pb-1 bg-gradient-to-r from-[#FFFFFF66] via-white to-[#FFFFFF66] bg-clip-text text-transparent">
                Forgot Password
              </h1>
              <div className="shrink-0 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  aria-label="Go back"
                  className="inline-flex items-center justify-center p-2 text-white/70 transition hover:text-white"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 18l-6-6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <Link
                  to="/"
                  aria-label="Go to landing page"
                  className="inline-flex transform-gpu transition-all duration-300 hover:scale-[1.04] hover:drop-shadow-[0_14px_30px_rgba(72,111,255,0.35)]"
                >
                  <img src={logo} alt="logo" className="h-12 w-auto" />
                </Link>
              </div>
            </div>

            <p className="text-sm text-[#9B9DA7] animate-fadeInSoft" style={{ animationDelay: "220ms" }}>
              Enter your email and we&apos;ll send a reset link if your account exists.
            </p>

            <div className="mt-5 animate-fadeInSoft" style={{ animationDelay: "280ms" }}>
              {sent ? (
                <div className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 p-4 text-sm text-emerald-100/90">
                  If that email exists, a password reset link has been sent. Please check your email.
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error ? (
                    <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-100/90">
                      {error}
                    </div>
                  ) : null}
                  <div>
                    <label className="mb-2 block text-[#FFFFFF]">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                      }}
                      placeholder="Enter your email"
                      className="rdp-input bg-[#04061640] border border-[#EBEBEB1C] placeholder:text-[#FFFFFF78] py-4"
                      required
                      autoComplete="email"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-1 block w-[80%] mx-auto bg-[#FFFFFF0F] rounded-xl py-2.5 text-sm font-medium text-white
                    shadow-[0_2px_4px_#00000040,inset_0_1px_1px_#FFFFFF3B,inset_0_-16px_28px_#FFFFFF26]
                    transition-all duration-300 hover:bg-[#FFFFFF14] hover:shadow-[0_10px_22px_#00000050,inset_0_1px_1px_#FFFFFF4D,inset_0_-16px_28px_#FFFFFF2E]
                    disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              )}
            </div>

            <p className="mt-5 text-center text-sm text-white">
              Remembered your password?{" "}
              <Link to="/signin" className="font-semibold text-[#486FFF]">
                Log In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
